// Cook-mode timers. Pure where it can be; the alarm at the bottom is the
// only thing that touches the browser.
//
// Several can run at once (lesson 8 is three clocks), each is named after
// the step that started it, and a timer that ends keeps ringing until it is
// dismissed. Time is measured against the wall clock, not counted down, so
// a backgrounded tab (iOS suspends timers freely) comes back showing the
// truth.

export const createTimer = ({ id, label, seconds, stepIndex }, now = Date.now()) => ({
  id: id || `${now}-${Math.random().toString(16).slice(2, 8)}`,
  label,
  seconds,
  stepIndex,
  startedAt: now,
  endsAt: now + seconds * 1000,
  dismissed: false
});

export const remaining = (timer, now = Date.now()) => Math.max(0, Math.ceil((timer.endsAt - now) / 1000));

export const isDone = (timer, now = Date.now()) => now >= timer.endsAt;

export const formatSeconds = (total) => {
  const s = Math.max(0, Math.round(total));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}:${String(m % 60).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  }
  return `${m}:${String(r).padStart(2, '0')}`;
};

/** Add a minute (or any span) to a running or finished timer. */
export const extend = (timer, seconds, now = Date.now()) => ({
  ...timer,
  endsAt: Math.max(timer.endsAt, now) + seconds * 1000,
  dismissed: false
});

// --- the alarm ---------------------------------------------------------------

let audioContext = null;

/** A short three-note chime through Web Audio. Needs a prior user gesture on iOS. */
export function chime () {
  try {
    audioContext = audioContext || new (window.AudioContext || window.webkitAudioContext)();
    const now = audioContext.currentTime;
    [880, 1108, 1318].forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.4, now + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.18 + 0.5);
      osc.connect(gain).connect(audioContext.destination);
      osc.start(now + i * 0.18);
      osc.stop(now + i * 0.18 + 0.55);
    });
  } catch {
    // No audio: the screen still shows the timer flashing.
  }
}

/** Warm the audio context on the first tap, so later chimes are allowed. */
export function primeAudio () {
  try {
    audioContext = audioContext || new (window.AudioContext || window.webkitAudioContext)();
    if (audioContext.state === 'suspended') audioContext.resume();
  } catch { /* no audio */ }
}

/** A local notification when the page is hidden. Not push; the page must be alive. */
export async function notifyDone (timer) {
  try {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') await Notification.requestPermission();
    if (Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible') return;
    const registration = await navigator.serviceWorker?.getRegistration?.();
    const options = { body: `${timer.label} is done.`, tag: `timer-${timer.id}` };
    if (registration?.showNotification) await registration.showNotification('Enjoy Cooking', options);
    else new Notification('Enjoy Cooking', options);
  } catch { /* best effort */ }
}
